import * as React from 'react';
import { Dropdown, Form } from 'semantic-ui-react';
import { useEffect, useState } from 'react';
import { ApiRequest } from 'GlobalFunctions';
import debounce from 'lodash/debounce';
import { VscClose } from 'react-icons/vsc';


export default function AutocompleteField_su(props: any) {
  const {
    endpoint = "",
    endpoint_params = {},
    endpoint_method = "get",
    label,
    value,
    onChange,
    fullWidth,
    dynamic = false,
    max_results = null,
    clearOnSelect = false,
    multiple = false,
    size = 'medium',
    readOnly = false,
    className = '',
    error = false,
    helperText = '',
    disabled = false,
    onOpen = () => {}, // Add onOpen prop with default empty function
    onClose = () => {}, // Add onClose prop with default empty function
    mode = 'autocomplete', // Add mode prop with default value
    allowClear = false, // Add allowClear prop to enable clear functionality
  } = props;

  const [list, setList] = useState<any>([]);
  const [options, setOptions] = useState<any>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedValue, setSelectedValue] = useState<any>(multiple ? [] : '');
  const [hasUserTyped, setHasUserTyped] = useState(false); // Track if user has actively typed
  const [initialSearchQuery, setInitialSearchQuery] = useState(''); // Track initial state

  // Initial data fetch
  useEffect(() => {
    getList();
    setSelectedValue(multiple ? [] : '');
  }, [JSON.stringify(endpoint_params)]);

  // Transform list to Semantic UI options format
  useEffect(() => {
    if (list && list.length > 0) {
      const transformedOptions = list.map((item: any) => ({
        key: item.value,
        value: item.value,
        text: item.label
      }));
      setOptions(transformedOptions);
    }
  }, [list]);

  // Update selected value when value prop changes
  useEffect(() => {
    if (!multiple) {
      if (value) {
        const newSelectedValue = typeof value === 'object' ? value.value : value;
        setSelectedValue(newSelectedValue);
        
        // Update searchQuery to match the selected value's label
        if (list && list.length > 0) {
          const selectedItem = list.find((item: any) => item.value === newSelectedValue);
          if (selectedItem) {
            setSearchQuery(selectedItem.label);
          }
        }
      } else {
        setSelectedValue('');
        setSearchQuery('');
        setHasUserTyped(false); // Reset when value changes externally
      }
    } else {
      if (value && Array.isArray(value)) {
        setSelectedValue(value.map((v: any) => typeof v === 'object' ? v.value : v));
      } else {
        setSelectedValue([]);
        setHasUserTyped(false); // Reset when value changes externally
      }
    }
  }, [value, multiple, list]);

  // Sync searchQuery with selectedValue when options are available
  useEffect(() => {
    if (!multiple && selectedValue && options.length > 0 && !hasUserTyped) {
      const selectedOption = options.find((option: any) => option.value === selectedValue);
      if (selectedOption && searchQuery !== selectedOption.text) {
        setSearchQuery(selectedOption.text);
      }
    }
  }, [selectedValue, options, hasUserTyped, multiple, searchQuery]);

  /*=========================================================
    GET LIST
  =========================================================*/
  const getList = async (limit: number | null = null, inputValueParam: string = "") => {
    const filters = dynamic && inputValueParam !== ''
      ? [{ key: 'city_name', value: inputValueParam, action: 'search', condition: '=' }]
      : undefined;

    const params = {
      full_list: limit === null,
      max_results: limit || undefined,
      ...endpoint_params,
      filters,
    };
    
    await ApiRequest({
      url: endpoint,
      method: endpoint_method.toLowerCase(), // Use the provided endpoint_method
      headers: {
        ltkn: localStorage.getItem('ltkn'),
        store_id: localStorage.getItem('store_id') || undefined,
      },
      // Use query for GET method, body for other methods (POST, PUT, etc.)
      ...(endpoint_method.toLowerCase() === 'get' 
        ? { query: params } 
        : { body: params }),
      setResponse: (response: any) => {
        setList(response.list || []);
      },
    });
  };

  // Debounced function to fetch limited results based on max_results
  const debouncedGetList = debounce((inputValueParam: string) => {
    getList(max_results, inputValueParam);
  }, 500);

  /*=========================================================
    HANDLE SEARCH CHANGE
  =========================================================*/
  const handleSearchChange = (e: any, { searchQuery }: any) => {
    setSearchQuery(searchQuery);
    
    // Only mark as user typed if the search query is actually different from initial state
    // and it's not just the display text of an already selected item
    const currentSelectedOption = options.find((option: any) => option.value === selectedValue);
    const isCurrentSelectionText = currentSelectedOption && currentSelectedOption.text === searchQuery;
    
    if (searchQuery !== initialSearchQuery && !isCurrentSelectionText) {
      setHasUserTyped(true);
    }

    if (dynamic) {
      if (searchQuery === '') {
        // Input cleared, cancel pending calls and fetch full list
        debouncedGetList.cancel();
        getList(max_results, '');
      } else {
        debouncedGetList(searchQuery);
      }
    }
  };

  /*=========================================================
    HANDLE CLEAR
  =========================================================*/
  const handleClear = () => {
    setSelectedValue(multiple ? [] : '');
    setSearchQuery('');
    setHasUserTyped(false);
    onChange(multiple ? [] : null);
    if (dynamic) {
      getList(max_results, '');
    }
  };

  /*=========================================================
    HANDLE SELECT
  =========================================================*/
  const handleChange = (e: any, { value: newValue }: any) => {
    setSelectedValue(newValue);
    setHasUserTyped(false); // Reset after selection
    
    if (!multiple) {
      // Handle clearing when value is empty, null, or undefined
      if (newValue === '' || newValue === null || newValue === undefined) {
        setSearchQuery('');
        onChange(null);
        return;
      }
      
      const selectedItem = list.find((item: any) => item.value === newValue);
      
      if (selectedItem) {
        // Always update the search query to the label IMMEDIATELY
        setSearchQuery(selectedItem.label);
        onChange(selectedItem);
        
        // If the item isn't in the list, add it.
        if (!list.some((i: any) => i.value === selectedItem.value)) {
          setList([...list, selectedItem]);
        }
      } else {
        // If no item found, still call onChange with null but keep the value
        onChange(null);
      }
      
      // Avoid re-fetching the list in single mode
      if (clearOnSelect) {
        debouncedGetList.cancel();
      }
    } else {
      const selectedItems = list.filter((item: any) => newValue.includes(item.value));
      onChange(selectedItems || []);
      // Clear search query after selection in multiple mode
      setSearchQuery('');
      if (clearOnSelect) {
        debouncedGetList.cancel();
        getList(max_results, '');
      }
    }
  };

  /*=========================================================
    RETURN
  =========================================================*/
  return (
    <Form.Field error={error}>
      <div style={{ position: 'relative' }}>
        {mode === 'select' ? (
          <Dropdown
            fluid={fullWidth}
            className={className}
            placeholder={label}
            selection
            multiple={multiple}
            options={options}
            value={selectedValue}
            onChange={handleChange}
            disabled={disabled || readOnly}
            noResultsMessage="No results found"
            size={size === 'small' ? 'mini' : size === 'medium' ? 'small' : 'large'}
            clearable={false} // Disable native clear functionality
            // No search, no searchQuery, no onSearchChange
            searchInput={{ autoComplete: 'off' }}
            onOpen={() => onOpen()}
            onClose={() => onClose()}
          />
        ) : (
          <Dropdown
            fluid={fullWidth}
            className={className}
            placeholder={label}
            search
            selection
            multiple={multiple}
            options={options}
            value={selectedValue} 
            onChange={handleChange}
            onSearchChange={handleSearchChange}
            searchQuery={searchQuery}
            disabled={disabled || readOnly}
            noResultsMessage="No results found"
            size={size === 'small' ? 'mini' : size === 'medium' ? 'small' : 'large'}
            searchInput={{ autoComplete: 'off' }}
            clearable={false} // Disable native clear functionality
            selectOnNavigation={false} // Prevent auto-selection on arrow keys
            selectOnBlur={false} // Prevent auto-selection on blur
            onOpen={() => {
              setHasUserTyped(false); // Reset when dropdown opens
              setInitialSearchQuery(searchQuery); // Store initial state
              onOpen();
            }}
            onClose={() => {
              if (!multiple && !hasUserTyped) {
                // If user hasn't actively typed, just ensure searchQuery matches selected value
                if (selectedValue) {
                  const selectedOption = options.find((option: any) => option.value === selectedValue);
                  if (selectedOption && searchQuery !== selectedOption.text) {
                    setSearchQuery(selectedOption.text);
                  }
                } else if (!selectedValue && searchQuery) {
                  setSearchQuery('');
                }
              }
              
              setHasUserTyped(false); // Reset after close
              setInitialSearchQuery(''); // Reset initial state
              onClose(); // Call the original onClose handler
            }}
          />
        )}
        
        {/* Custom Clear Button */}
        {allowClear && !disabled && !readOnly && ((multiple && selectedValue.length > 0) || (!multiple && selectedValue !== '' && selectedValue !== null && selectedValue !== undefined)) && (
          <button
            type="button"
            onClick={handleClear}
            style={{
              position: 'absolute',
              right: '30px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '2px',
              color: '#999',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              fontSize: '14px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#333'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#999'}
          >
            <VscClose />
          </button>
        )}
      </div>
      {helperText && (
        <div className={error ? "ui pointing red basic label" : "ui pointing label"}>
          {helperText}
        </div>
      )}
    </Form.Field>
  );
}
